import { useEffect, useState } from "react";
import { api } from "./api";

let CACHE = null;
let PENDING = null;

/**
 * Fetch (and cache for the rest of the session) the Ontario Grades 4-8 curriculum
 * index from the backend. Returns:
 *   { loading, data: { grades, subjects_by_grade, strands } | null, error }
 */
export const useCurriculum = () => {
  const [data, setData] = useState(CACHE);
  const [loading, setLoading] = useState(!CACHE);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (CACHE) return;
    let cancelled = false;
    if (!PENDING) {
      PENDING = api.get("/curriculum").then((r) => r.data);
    }
    PENDING.then((d) => {
      if (cancelled) return;
      CACHE = d;
      setData(d);
      setLoading(false);
    }).catch((e) => {
      if (cancelled) return;
      PENDING = null;
      setError(e);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return { data, loading, error };
};

// Expand a combined grade string ("Grade 5/6") into its component
// single-grade keys (["Grade 5","Grade 6"]). Single grades pass through
// unchanged, so every caller keeps working for the normal case.
export const expandGrades = (grade) => {
  const nums = String(grade || "").match(/[4-8]/g) || [];
  const uniq = [...new Set(nums)];
  if (uniq.length <= 1) return [grade];
  return uniq.map((n) => `Grade ${n}`);
};
export const isCombinedGrade = (grade) => expandGrades(grade).length > 1;

export const getGradesFromCurriculum = (d) => d?.grades || [];

export const getSubjectsForGrade = (d, grade) => {
  const out = [];
  for (const g of expandGrades(grade)) {
    for (const s of (d?.subjects_by_grade?.[g] || [])) if (!out.includes(s)) out.push(s);
  }
  return out;
};

export const getStrandsForGradeSubject = (d, grade, subject) => {
  const out = [];
  const seen = new Set();
  for (const g of expandGrades(grade)) {
    for (const s of ((d?.strands?.[g] || {})[subject] || [])) {
      if (!seen.has(s.code)) { seen.add(s.code); out.push(s); }
    }
  }
  return out;
};

export const getSpecificsForGradeSubject = (d, grade, subject) => {
  const grades = expandGrades(grade);
  if (grades.length <= 1) {
    // Single grade — return the exact stored rows (no grade tag) so
    // existing single-grade behaviour is byte-identical.
    return ((d?.specific_expectations?.[grades[0]] || {})[subject] || []);
  }
  // Combined grade — concat both grades, tagging each row with the grade
  // it came from so the picker can show + identify each separately.
  const out = [];
  for (const g of grades) {
    const num = (g.match(/[4-8]/) || [""])[0];
    for (const row of ((d?.specific_expectations?.[g] || {})[subject] || [])) {
      out.push({ ...row, grade: g, gradeNum: num });
    }
  }
  return out;
};

